import { Component } from '@theme/component';
import { ConstructorGrid } from '@theme/constructor-grid';
import { query, extractResultId } from '@theme/constructor-client';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Constructor recommendations pod.
 *
 * Defers until near the viewport, then asks Constructor for one pod and hands
 * the resulting product ids to the base class. It renders no markup of its own
 * — every card comes from Liquid.
 *
 * @extends {ConstructorGrid}
 */
class ConstructorRecs extends ConstructorGrid {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /**
   * `response.pod.id`, written onto the container as
   * `data-cnstrc-recommendations-pod-id`. Pod identity lives on the container
   * and strategy identity on each item — two different DOM levels, because
   * one pod's results can come from more than one strategy. Neither can stand
   * in for the other.
   * @type {string}
   */
  resolvedPodId = '';

  /**
   * Comma-separated seed item ids, when the pod was asked for some.
   * @type {string}
   */
  seedItems = '';

  /**
   * Shopify product id -> `results[i].strategy.id`.
   *
   * Keyed by id rather than by position: a ranked id whose product is not
   * published to this market is simply absent from the rendered cards, so card
   * index and result index drift apart the moment one is missing.
   * @type {Map<string, string>}
   */
  #strategyById = new Map();

  connectedCallback() {
    Component.prototype.connectedCallback.call(this);

    this.addEventListener(SlideshowSelectEvent.eventName, this.#updateArrows);

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#observer = undefined;
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.removeEventListener(SlideshowSelectEvent.eventName, this.#updateArrows);
  }

  /**
   * The carousel's `<slideshow-component>`, when the section uses the carousel layout.
   * @returns {(HTMLElement & { previous: () => void, next: () => void, current: number, nextIndex: number, slides?: HTMLElement[] }) | null}
   */
  get #carousel() {
    return this.querySelector('slideshow-component');
  }

  previousSlide() {
    this.#carousel?.previous();
  }

  nextSlide() {
    this.#carousel?.next();
  }

  #updateArrows = () => {
    const carousel = this.#carousel;
    const { previousButton, nextButton } = /** @type {Record<string, HTMLButtonElement | undefined>} */ (this.refs);
    if (!carousel || !previousButton || !nextButton) return;

    previousButton.disabled = carousel.current <= 0;
    nextButton.disabled = carousel.nextIndex >= (carousel.slides?.length ?? 0);
  };

  /**
   * Fill the server-rendered slots (carousel or grid) instead of rebuilding them.
   * @param {Element[]} cards
   * @returns {boolean}
   */
  placeCards(cards) {
    const slots = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-recs-slot]')]);
    if (!slots.length) return super.placeCards(cards);

    slots.forEach((slot, index) => {
      const card = cards[index];
      const item = slot.closest('slideshow-slide') ?? slot.closest('.resource-list__item');

      slot.replaceChildren(...(card ? [card] : []));
      item?.toggleAttribute('hidden', !card);
    });

    requestAnimationFrame(this.#updateArrows);

    return true;
  }

  /**
   * Container attributes specific to a recommendations pod.
   * @returns {Record<string, string | number | undefined>}
   */
  beaconExtras() {
    return {
      podId: this.resolvedPodId,
      seedItems: this.seedItems,
    };
  }

  /** @returns {import('@theme/constructor-tracker').ResultContext} */
  trackingContext() {
    return {
      ...super.trackingContext(),
      podId: this.resolvedPodId,
      seedItemIds: this.seedItems ? this.seedItems.split(',') : undefined,
    };
  }

  /**
   * Adds the per-item strategy id on top of the base marker.
   * @param {Element} card
   * @param {number} index
   */
  decorateCard(card, index) {
    super.decorateCard(card, index);
    const itemId = card.getAttribute('data-cnstrc-item-id');
    const strategyId = itemId ? this.#strategyById.get(itemId) : undefined;
    if (strategyId) card.setAttribute('data-cnstrc-strategy-id', strategyId);
  }

  /** @returns {Promise<(string | number)[]>} */
  async requestIds() {
    const podId = this.dataset.podId;
    if (!podId) return [];

    const seedIds = this.dataset.productId ? [this.dataset.productId] : undefined;

    const { data } = await query(this.surface, (client, signal) =>
      client.recommendations.getRecommendations(
        podId,
        {
          itemIds: seedIds,
          numResults: Number(this.dataset.podLimit) || 4,
        },
        { signal }
      )
    );

    /** @type {{ data?: { id?: string }, strategy?: { id?: string } }[]} */
    const results = data?.response?.results ?? [];

    this.resultId = extractResultId(data);
    this.resolvedPodId = data?.response?.pod?.id || podId;
    this.seedItems = seedIds?.join(',') ?? '';
    this.totalResults = data?.response?.total_num_results ?? results.length;

    this.#strategyById = new Map();
    for (const result of results) {
      const id = result?.data?.id;
      const strategy = result?.strategy?.id;
      if (id && strategy) this.#strategyById.set(String(id), strategy);
    }

    return results
      .map((result) => result?.data?.id)
      .filter(/** @returns {id is string} */ (id) => id != null);
  }
}

if (!customElements.get('constructor-recs-component')) {
  customElements.define('constructor-recs-component', ConstructorRecs);
}
